import { copilotStudioScope, copilotStudioTenantId } from "@framework/constants/copilot-studio"

const VERIFIER_KEY = "copilot-studio-pkce-verifier"
const TOKEN_KEY = "copilot-studio-access-token"
const AUTH_MODE_KEY = "copilot-studio-auth-mode"
const LOGIN_HINT_KEY = "copilot-studio-login-hint"

export type CopilotAuthMode = "silent" | "interactive"

type StoredToken = {
  accessToken: string
  expiresAt: number
  refreshToken?: string
}

export function copilotStudioRedirectUri() {
  return `${window.location.origin}/copilot`
}

function base64Url(bytes: Uint8Array) {
  let binary = ""
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}

async function createPkce() {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  const verifier = base64Url(bytes)
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  )
  const challenge = base64Url(new Uint8Array(digest))
  return { verifier, challenge }
}

export function readCopilotStudioToken(): string | null {
  const raw = sessionStorage.getItem(TOKEN_KEY)
  if (!raw) return null
  try {
    const stored = JSON.parse(raw) as StoredToken
    if (!stored.accessToken || stored.expiresAt <= Date.now() + 60_000) {
      return null
    }
    return stored.accessToken
  } catch {
    return null
  }
}

export function clearCopilotStudioToken() {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(AUTH_MODE_KEY)
  sessionStorage.removeItem(LOGIN_HINT_KEY)
}

export function copilotStudioAuthMode(): CopilotAuthMode | null {
  const mode = sessionStorage.getItem(AUTH_MODE_KEY)
  return mode === "silent" || mode === "interactive" ? mode : null
}

export function rememberCopilotLoginHint(loginHint?: string) {
  const hint = loginHint?.trim()
  if (hint) sessionStorage.setItem(LOGIN_HINT_KEY, hint)
}

export function readCopilotLoginHint() {
  return sessionStorage.getItem(LOGIN_HINT_KEY) || ""
}

export async function beginCopilotStudioSignIn(
  clientId: string,
  options?: { silent?: boolean; loginHint?: string }
) {
  const { verifier, challenge } = await createPkce()
  sessionStorage.setItem(VERIFIER_KEY, verifier)
  const silent = Boolean(options?.silent)
  sessionStorage.setItem(AUTH_MODE_KEY, silent ? "silent" : "interactive")
  rememberCopilotLoginHint(options?.loginHint)
  const url = new URL(
    `https://login.microsoftonline.com/${copilotStudioTenantId}/oauth2/v2.0/authorize`
  )
  url.searchParams.set("client_id", clientId)
  url.searchParams.set("response_type", "code")
  url.searchParams.set("redirect_uri", copilotStudioRedirectUri())
  url.searchParams.set("response_mode", "query")
  url.searchParams.set("scope", copilotStudioScope)
  url.searchParams.set("code_challenge", challenge)
  url.searchParams.set("code_challenge_method", "S256")
  const loginHint = options?.loginHint?.trim() || readCopilotLoginHint()
  if (loginHint) url.searchParams.set("login_hint", loginHint)
  if (silent) url.searchParams.set("prompt", "none")
  window.location.assign(url.href)
}

export function copilotSilentSignInNeedsInteraction(error: string | null) {
  return (
    error === "login_required" ||
    error === "interaction_required" ||
    error === "consent_required"
  )
}

async function requestToken(body: URLSearchParams) {
  const response = await fetch(
    `https://login.microsoftonline.com/${copilotStudioTenantId}/oauth2/v2.0/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    }
  )
  const payload = (await response.json()) as {
    access_token?: string
    expires_in?: number
    refresh_token?: string
    error?: string
    error_description?: string
  }
  if (!response.ok || !payload.access_token) {
    throw new Error(
      payload.error_description || payload.error || "Microsoft sign-in failed"
    )
  }
  const stored: StoredToken = {
    accessToken: payload.access_token,
    expiresAt: Date.now() + (payload.expires_in ?? 3600) * 1000,
    refreshToken: payload.refresh_token,
  }
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(stored))
  return stored.accessToken
}

export async function completeCopilotStudioSignIn(
  clientId: string,
  code: string
) {
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  if (!verifier) {
    throw new Error("Sign-in expired. Start Microsoft sign-in again.")
  }
  const body = new URLSearchParams({
    client_id: clientId,
    grant_type: "authorization_code",
    code,
    redirect_uri: copilotStudioRedirectUri(),
    code_verifier: verifier,
    scope: copilotStudioScope,
  })
  const token = await requestToken(body)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(AUTH_MODE_KEY)
  return token
}

export async function refreshCopilotStudioToken(clientId: string) {
  const raw = sessionStorage.getItem(TOKEN_KEY)
  if (!raw) return null
  let stored: StoredToken
  try {
    stored = JSON.parse(raw) as StoredToken
  } catch {
    return null
  }
  if (stored.accessToken && stored.expiresAt > Date.now() + 60_000) {
    return stored.accessToken
  }
  if (!stored.refreshToken) return null
  const body = new URLSearchParams({
    client_id: clientId,
    grant_type: "refresh_token",
    refresh_token: stored.refreshToken,
    scope: copilotStudioScope,
  })
  try {
    return await requestToken(body)
  } catch {
    sessionStorage.removeItem(TOKEN_KEY)
    return null
  }
}
